import { errorToast } from "@/v2-ui/toast"
import {
  ConfirmButton,
  TooltipButton,
} from "@/v2-backoffice-client/components/button"
import { useCustomerMergeConsolidateMutation } from "@/v2-backoffice-client/customer/merge/customer.merge.consolidate.mutation"
import { useCustomerMergeKeepSeparateMutation } from "@/v2-backoffice-client/customer/merge/customer.merge.keepSeparate.mutation"
import {
  CUSTOMER_MERGE_ACTION_CONSOLIDATED,
  CUSTOMER_MERGE_STATUS_AWAITING_SYNC,
  CUSTOMER_MERGE_STATUS_IN_PROGRESS,
  CUSTOMER_MERGE_STATUS_KEPT_SEPARATE,
  CUSTOMER_MERGE_STATUS_NEEDS_DECISION,
  CUSTOMER_MERGE_STATUS_PENDING,
  CUSTOMER_MERGE_STATUS_SETTLED,
  describeHoldings,
  describeMergeBlocker,
  formatMergeSide,
  holdsMoney,
} from "@/v2-backoffice-client/customer/merge/customer.merge.utils"
import type {
  CustomerMergeFragment,
  CustomerMergeSideFragment,
} from "@/v2-backoffice-client/customer/merge/__types__/customer.merge.fragment"

export type CustomerMergeResolveCellProps = {
  merge: CustomerMergeFragment
  /** The row changed; the board refetches. */
  onResolved?(): void
}

/**
 * The Decide cell: the operator's choices for one merge, each a confirm
 * away. A merge the Backoffice is about to consolidate shows both keepers
 * side by side - the database's choice filled, the other outlined - and
 * Keep separate; one that needs a decision shows what blocks it, the
 * keeper buttons greyed with the reason, and Keep separate; one that was
 * consolidated offers the undo (Keep separate moves everything back); one
 * kept separate offers to merge after all. In progress and awaiting the
 * sync, nothing can be clicked and the cell says why.
 */
function CustomerMergeResolveCell(props: CustomerMergeResolveCellProps) {
  const { merge, onResolved } = props
  const leftover = merge.LeftoverCustomer
  const survivor = merge.SurvivingCustomer
  const status: string = merge.Status

  // Each write resolves with its data only when it went through
  // (errorPolicy is "all" app-wide), so the row is reported as changed
  // only then.
  const [consolidate, { loading: consolidating, error: consolidateError }] =
    useCustomerMergeConsolidateMutation()
  const [keepSeparate, { loading: keepingSeparate, error: keepError }] =
    useCustomerMergeKeepSeparateMutation()
  errorToast(consolidateError, { title: "The merge was not consolidated" })
  errorToast(keepError, { title: "The rows were not kept separate" })

  async function consolidateInto(keeper: CustomerMergeSideFragment) {
    const result = await consolidate({
      variables: {
        externalCompanyId: merge.ExternalCompanyId,
        keeperCustomerId: keeper.Id,
      },
    })
    if (result.data?.consolidateCustomerMerge) onResolved?.()
  }

  async function keepRowsSeparate() {
    const result = await keepSeparate({
      variables: { externalCompanyId: merge.ExternalCompanyId },
    })
    if (result.data?.keepCustomerMergeSeparate) onResolved?.()
  }

  if (
    status === CUSTOMER_MERGE_STATUS_IN_PROGRESS ||
    status === CUSTOMER_MERGE_STATUS_AWAITING_SYNC
  ) {
    const content =
      status === CUSTOMER_MERGE_STATUS_IN_PROGRESS
        ? "An action on this merge is being carried out; it finishes within minutes, and a run that died is finished by the next one."
        : "The surviving company has no customer with content yet. The hourly CRM sync links or creates one, or retires the empty row holding it; nothing can be decided until then."
    return (
      <TooltipButton
        size="xs"
        variant="outline"
        iconSrc="hourglass"
        disabled
        content={content}
        data-feature-code="UI_BackofficeCustomer_Merges_Waiting"
      >
        {status === CUSTOMER_MERGE_STATUS_IN_PROGRESS
          ? "In progress"
          : "Waiting for the sync"}
      </TooltipButton>
    )
  }

  // Settled with a consolidation on record: the undo. The action names the
  // row the people come back to (after a swap the row merged away holds
  // the company and is no leftover any more, so the action, not the
  // leftover, is the source).
  if (status === CUSTOMER_MERGE_STATUS_SETTLED) {
    const action = merge.LatestAction
    const actionCode: string | undefined = action?.Code
    if (!action || actionCode !== CUSTOMER_MERGE_ACTION_CONSOLIDATED) {
      return (
        <span className="text-xs text-muted-foreground">Nothing to do</span>
      )
    }
    const moved = describeMovedBack(
      action.TenantIds.length,
      action.UserIds.length,
      action.MovesLink,
    )
    const backTo = formatMergeSide(action.FromCustomer)
    return (
      <ConfirmButton
        size="xs"
        variant="outline"
        iconSrc="undo"
        isLoading={keepingSeparate}
        content={`Undo the consolidation: ${moved} come back to ${backTo}, and the two rows stay separate customers (the one without the company has no CRM record). HubSpot's merge itself cannot be undone.`}
        confirmContent={`Move ${moved} back to ${backTo} and keep the two rows as separate customers?`}
        confirmVariant="warning"
        onConfirm={keepRowsSeparate}
        data-feature-code="UI_BackofficeCustomer_Merges_Undo"
      >
        Undo
      </ConfirmButton>
    )
  }

  if (!leftover || !survivor) {
    return <span className="text-xs text-muted-foreground">Nothing to do</span>
  }

  const blocker = describeMergeBlocker(merge.Blocker)
  const keeperId = merge.KeeperCustomer?.Id

  function renderKeeper(
    keeper: CustomerMergeSideFragment,
    isLeftover: boolean,
  ) {
    const emptied = isLeftover ? survivor! : leftover!
    const emptiedHoldings = isLeftover
      ? merge.SurvivorHoldings
      : merge.LeftoverHoldings
    const keeperHoldings = isLeftover
      ? merge.LeftoverHoldings
      : merge.SurvivorHoldings
    const isSuggested = keeper.Id === keeperId
    // A keeper whose counterpart holds money or config cannot be picked:
    // the same rule the automation follows, told here instead of by a
    // refused write.
    const disabledReason = holdsMoney(emptiedHoldings)
      ? `${formatMergeSide(emptied)} holds a licence or a billing account; end or relink it first, or keep ${formatMergeSide(emptied)} instead.`
      : (emptiedHoldings?.Config ?? 0) > 0
        ? `${formatMergeSide(emptied)} holds an add-on, an access list, a closed feature class or a collections owner; move or clear it first.`
        : status === CUSTOMER_MERGE_STATUS_NEEDS_DECISION &&
            isSuggested &&
            blocker
          ? blocker
          : null
    const link = isLeftover ? " The surviving company's link follows." : ""
    const why = holdsMoney(keeperHoldings)
      ? " It holds the money."
      : isLeftover
        ? ""
        : " HubSpot kept its company."
    return (
      <ConfirmButton
        size="xs"
        variant={isSuggested && !disabledReason ? "suggested" : "outline"}
        iconSrc={isLeftover ? "arrow-left" : "arrow-right"}
        isLoading={consolidating}
        disabled={Boolean(disabledReason)}
        content={
          disabledReason ??
          `Keep ${formatMergeSide(keeper)}: ${describeHoldings(emptiedHoldings)} of ${formatMergeSide(emptied)} move onto it, on v1 and v2; licenses and seats follow.${link}${why}${isSuggested ? " This is what the Backoffice would do on its own." : ""}`
        }
        confirmContent={`Keep ${formatMergeSide(keeper)} and move ${describeHoldings(emptiedHoldings)} of ${formatMergeSide(emptied)} onto it?${link}`}
        confirmVariant="suggested"
        onConfirm={() => consolidateInto(keeper)}
        data-feature-code="UI_BackofficeCustomer_Merges_Keep"
      >
        Keep {formatMergeSide(keeper)}
      </ConfirmButton>
    )
  }

  const keepSeparateLabel =
    status === CUSTOMER_MERGE_STATUS_KEPT_SEPARATE ? null : (
      <ConfirmButton
        size="xs"
        variant="ghost"
        iconSrc="arrows-split-up-and-left"
        isLoading={keepingSeparate}
        content={`${formatMergeSide(leftover)} stays a customer of its own without a CRM company: nothing moves, the pickers offer it again, and the Backoffice leaves this merge alone from now on.`}
        confirmContent={`Keep ${formatMergeSide(leftover)} and ${formatMergeSide(survivor)} as two separate customers?`}
        onConfirm={keepRowsSeparate}
        data-feature-code="UI_BackofficeCustomer_Merges_KeepSeparate"
      >
        Keep separate
      </ConfirmButton>
    )

  // The two keepers side by side (the suggested one filled, the other
  // outlined), the third way out quiet under them: one glance says what the
  // Backoffice would do, what else is possible, and what the escape is.
  return (
    <span className="flex flex-col items-start gap-1">
      <span className="flex flex-wrap items-center gap-1.5 [&>*]:shrink-0">
        {renderKeeper(survivor, false)}
        {renderKeeper(leftover, true)}
      </span>
      {keepSeparateLabel}
    </span>
  )
}

function describeMovedBack(tenants: number, users: number, link: boolean) {
  const parts = [
    tenants > 0 ? `${tenants} team${tenants === 1 ? "" : "s"}` : null,
    users > 0 ? `${users} user${users === 1 ? "" : "s"}` : null,
    link ? "the company link" : null,
  ].filter((part): part is string => part !== null)
  return parts.length > 0 ? parts.join(", ") : "nothing"
}

export default CustomerMergeResolveCell
